import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import { TableBody, TableRow } from "@kaltura/mediaspace-shared-styled";
import { Alert, Checkbox, IconButton, Pagination, Table, TableContainer, Tooltip } from "@kaltura/ds-react-components";
import { Visible24Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { columnsMap } from "./columns";
import { SearchAndSelectColumnId } from "./SearchAndSelectColumnId";
import { MediaTableHeader } from "./MediaTableHeader";
import { ConditionalNotice, MediaTableProps } from "./MediaTableProps";
import {
    actionsColumnWidth,
    StyledActionsContainer,
    StyledActionsTableCell,
    StyledCheckboxTableCell,
    StyledTableCell,
    StyledMessagesContainer,
} from "./styled";
import { MediaTableSkeleton } from "./MediaTableSkeleton";
import { NoResults } from "@kaltura/mediaspace-shared-search";
import { ErrorIllustration, StatusScreen } from "@kaltura/mediaspace-shared-ui";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType, SearchAndSelectEntry } from "@kaltura/mediaspace-shared-types";

export interface MediaTableRef {
    /** Focus the explore button in the index-th row */
    focusExploreButton: (index: number) => void;
}

/**
 * Table view of media list for Search and Select
 */
export const MediaTable = forwardRef<MediaTableRef, MediaTableProps>(
    (
        {
            entries = [],
            totalCount,
            isLoading,
            isError,
            pageSize,
            page,
            onPageChange,
            columns,
            actions,
            showExploreButton,
            onExplore,
            isKeywordSearch,
            stickyActionsWidth,
            selectionMode = "single",
            onSelectionChange,
            selectedItemsResetIndicator,
            conditionalMessages,
            columnsWidth,
            ...headerProps
        },
        ref
    ) => {
        const sendButtonAnalytics = useButtonAnalytics();
        const pagesCount = Math.ceil(totalCount / pageSize);
        const exploreButtonsRef = useRef<(HTMLButtonElement | null)[]>([]);
        // state for messages
        const [messages, setMessages] = useState<ReactNode>(null);
        // state for keeping selected items ids
        const [selectedItems, setSelectedItems] = useState<string[]>([]);

        useImperativeHandle(
            ref,
            () => ({
                focusExploreButton: (index) => {
                    exploreButtonsRef.current[index]?.focus();
                },
            })
        );

        const handleCheckboxChange = (checked: boolean, entryId: string) => {
            const newSelectedItems = checked
                ? [...selectedItems, entryId]
                : selectedItems.filter((id) => id !== entryId);
            setSelectedItems(newSelectedItems);
            onSelectionChange?.(newSelectedItems);
            const analyticsEventName = checked ? "checkbox - on" : "checkbox - off";
            sendButtonAnalytics(analyticsEventName, ButtonClickAnalyticsType.TOGGLE);
        };

        /**
         * only the entries that should be effected by the header checkbox un/selection
         */
        const entriesForHeaderCheckboxBehaviour = entries.filter(
            (entry) => (entry.readyForPublish ?? true) && !(entry.disabledChecked ?? false)
        );

        const handleCheckAllChange = (allSelected: boolean) => {
            const newSelectedItems = allSelected
                ? entriesForHeaderCheckboxBehaviour.map((entry) => entry.entry.id)
                : [];
            setSelectedItems(newSelectedItems);
            onSelectionChange?.(newSelectedItems);
            const analyticsEventName = allSelected ? "'all' checkbox - on" : "'all' checkbox - off";
            sendButtonAnalytics(analyticsEventName, ButtonClickAnalyticsType.TOGGLE);
        };

        // when entries change:
        useEffect(() => {
            const getRelevantConditionalMessages = (
                conditions: ConditionalNotice[],
                entries: SearchAndSelectEntry[]
            ) => {
                const matchedConditions = conditions.filter((condition) => entries.some(condition.predicate));
                if (matchedConditions.length === 0) return null;
                return matchedConditions.map((condition, idx) => (
                    <Alert key={idx} severity={condition.severity}>
                        {condition.message}
                    </Alert>
                ));
            };

            // calculate and show alert if there are disabled entries
            if (conditionalMessages) {
                setMessages(getRelevantConditionalMessages(conditionalMessages, entries));
            }
            // if there are any selected items reset selected items and notify parent
            if (selectedItems.length) {
                setSelectedItems([]);
                onSelectionChange?.([]);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [entries, conditionalMessages]); // `selectedItems` is not in the dependency array because this only has to run when `entries` change

        useEffect(() => {
            if (selectedItems.length) {
                setSelectedItems([]);
                onSelectionChange?.([]);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectedItemsResetIndicator]); // `selectedItems` is not in the dependency array because this only has to run when `entries` change

        return (
            <>
                {messages && <StyledMessagesContainer>{messages}</StyledMessagesContainer>}

                {isError && (
                    <StatusScreen
                        title={translate("Something went wrong")}
                        text={translate("We encountered an error, please reach out to the site admin. ")}
                        image={<ErrorIllustration size={200} />}
                    />
                )}

                {!isError && (
                    <>
                        <TableContainer rightStickyColumnWidth={actionsColumnWidth}>
                            <Table>
                                <MediaTableHeader
                                    columns={columns}
                                    selectionMode={selectionMode}
                                    allSelected={
                                        entries.length > 0
                                        && selectedItems.length === entriesForHeaderCheckboxBehaviour.length
                                    }
                                    onAllSelectChange={handleCheckAllChange}
                                    {...headerProps}
                                />

                                <TableBody>
                                    {isLoading && (
                                        <MediaTableSkeleton columns={columns} showExploreButton={showExploreButton} />
                                    )}

                                    {!isLoading
                                        && entries?.map((item, index) => (
                                            <TableRow key={item.entry.id}>
                                                {selectionMode === "multiple" && (
                                                    <StyledCheckboxTableCell stickyColumn={true} leadingColumn={false}>
                                                        <Checkbox
                                                            disabled={
                                                                !(item.readyForPublish ?? true)
                                                                || (item.disabledChecked ?? false)
                                                            }
                                                            checked={
                                                                selectedItems.includes(item.entry.id)
                                                                || (item.disabledChecked ?? false)
                                                            }
                                                            onChange={(event) => {
                                                                handleCheckboxChange(
                                                                    event.target.checked,
                                                                    item.entry.id
                                                                );
                                                            }}
                                                            inputProps={{
                                                                "aria-label": translate(
                                                                    "Select media %1",
                                                                    [item.entry.name]
                                                                ),
                                                            }}
                                                        />
                                                    </StyledCheckboxTableCell>
                                                )}
                                                {columns.map((columnId) => (
                                                    <StyledTableCell
                                                        key={columnId}
                                                        columnId={columnId}
                                                        leadingColumn={columnId === SearchAndSelectColumnId.name}
                                                        columnWidth={columnsWidth?.[columnId]}
                                                    >
                                                        {columnsMap[columnId].cell(item)}
                                                    </StyledTableCell>
                                                ))}
                                                {(showExploreButton || actions) && (
                                                    <StyledActionsTableCell
                                                        stickyColumn={true}
                                                        stickyWidth={stickyActionsWidth}
                                                    >
                                                        <StyledActionsContainer>
                                                            {showExploreButton && (
                                                                <Tooltip title={translate("Preview")} placement={"top"}>
                                                                    <IconButton
                                                                        ref={(el) => {
                                                                            exploreButtonsRef.current[index] = el;
                                                                        }}
                                                                        variant={"borderless"}
                                                                        color={"secondary"}
                                                                        onClick={() => onExplore?.(item, index)}
                                                                    >
                                                                        <Visible24Icon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}

                                                            {actions?.(item)}
                                                        </StyledActionsContainer>
                                                    </StyledActionsTableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {pagesCount > 1 && (
                            <Pagination
                                count={pagesCount}
                                page={page}
                                onChange={(_event, page) => onPageChange(page)}
                            />
                        )}
                        {!isLoading && !entries?.length && (
                            <NoResults size={"small"} screenMode={isKeywordSearch ? "nomatches" : "nopublic"} />
                        )}
                    </>
                )}
            </>
        );
    }
);
